/**
 * SiteGlue AI Solutions Consultant — shared types and persistence helpers.
 * Used by Trish Analyze-tab onboarding and the AI Employee Proposal.
 */

import type { AiEmployeePlanId } from "~/lib/siteglue/ai-employee-plans";
import {
  getSiteGlueIndustryBusinessOutcomes,
  resolveSiteGlueIndustry,
} from "~/lib/siteglue/industry-business-outcomes";

export type SchedulingRelevance = "high" | "moderate" | "low" | "not_applicable";

export type SchedulingLevel =
  | "none"
  | "appointment_request"
  | "basic"
  | "advanced"
  | "enterprise";

export type IntegrationPathKind = "supported" | "interim" | "custom_mcp";

export type ConsultantAgentPriority =
  | "recommended_first"
  | "high_value"
  | "optional"
  | "future";

export type CustomMcpRequestStatus =
  | "draft"
  | "submitted"
  | "under_review"
  | "discovery_required"
  | "api_access_required"
  | "approved"
  | "scheduled"
  | "in_development"
  | "testing"
  | "customer_validation"
  | "available"
  | "declined"
  | "blocked";

/** Expanded Trish Analyze-tab steps (SiteGlue consultant flow — PRD v2 order). */
export type SiteGlueConsultantStep =
  | "intake_industry"
  | "intake_goals"
  | "intake_channels"
  | "intake_scheduling"
  | "intake_platform"
  | "intake_demo"
  | "website"
  | "agent_profile"
  | "agent_job_title"
  | "analyzing"
  | "building_knowledge"
  | "snapshot"
  | "goals"
  | "report_draft"
  | "recommended_agents"
  | "budget"
  | "meet_employee"
  | "channels"
  | "scheduling_relevance"
  | "scheduling_method"
  | "scheduling_level"
  | "integration_path"
  | "appointment_roles"
  | "workflows"
  | "org_structure"
  | "plan"
  | "report"
  | "review"
  | "sign_up"
  | "complete";

export type EmployeePersonality =
  | "Friendly"
  | "Professional"
  | "Warm"
  | "Luxury"
  | "Technical"
  | "Custom";

export type EmployeePhotoChoice =
  | "Professional Female"
  | "Professional Male"
  | "Upload later";

export type BusinessSnapshotFinding = {
  id: string;
  label: string;
  category:
    | "location"
    | "team"
    | "channel"
    | "scheduling"
    | "portal"
    | "social"
    | "other";
  inferred: boolean;
  confirmed: boolean;
  removed?: boolean;
};

export type RecommendedAiEmployee = {
  roleName: string;
  businessNeed: string;
  channels: string[];
  schedulingResponsibility?: string;
  humanHandoff: string;
  teamTakeover: boolean;
  requiredIntegration?: string;
  expectedBenefit: string;
  priority: ConsultantAgentPriority;
};

export type EstimatedImpactItem = {
  category: string;
  rangeLabel: string;
  assumptions: string;
};

/** Outcome → Action mapping inside the AI Employee Proposal. */
export type DesiredBusinessOutcomePlan = {
  desiredBusinessOutcome: string;
  howEmployeeHelpsAchieveIt: string;
  recommendedActions: string[];
  recommendedChannels: string[];
  humanTeamCollaboration: string[];
  expectedBusinessImpact: string;
};

/** Recommended AI Employee card inside the AI Employee Proposal panel. */
export type StrategyRecommendedEmployee = {
  name: string;
  role: string;
  careerLevel: string;
  summary: string;
  profileImageUrl?: string;
};

/**
 * Customer-facing AI Employee Proposal payload (legacy internal name: AiOpportunityReportPayload).
 * Legacy scheduling fields may still be stored for session restore; UI must not show raw scores.
 */
export type AiOpportunityReportPayload = {
  executiveSummary: string;
  businessSnapshot: string[];
  businessGoals: string[];
  schedulingOpportunityScore: number;
  schedulingScoreWhy: string[];
  currentSchedulingMethod: string;
  recommendedSchedulingLevel: SchedulingLevel;
  recommendedAppointmentWorkflows: string[];
  recommendedAppointmentRoles: string[];
  recommendedChannels: string[];
  recommendedIntegrations: string[];
  integrationPath: IntegrationPathKind;
  interimWorkflow?: string;
  recommendedLocations: string[];
  recommendedDepartments: string[];
  recommendedTeamMembers: string[];
  humanInTheLoopStrategy: string[];
  recommendedPlan: AiEmployeePlanId;
  planReasons: string[];
  estimatedImpact: EstimatedImpactItem[];
  implementationPriorities: string[];
  nextSteps: string[];
  customMcpRequestId?: string;
  customMcpStatus?: CustomMcpRequestStatus;
  /** Proposal panel — dynamic title uses companyName. */
  companyName?: string;
  /** @deprecated Use proposalTitle — kept for session hydration. */
  strategyTitle?: string;
  proposalTitle?: string;
  preparedFor?: string;
  preparedBy?: string;
  subheading?: string;
  /** Structured outcome → action cards (one per confirmed desired outcome). */
  desiredOutcomePlans?: DesiredBusinessOutcomePlan[];
  nextStep?: string;
  futureCareerPath?: string[];
  priorities?: string[];
  whatILearned?: string[];
  keyInsights?: string[];
  recommendedEmployee?: StrategyRecommendedEmployee;
  recommendationRationale?: string[];
  howEmployeeHelps?: string[];
  humanTeamCollaboration?: string[];
  firstImplementation?: { startWith: string[]; considerAfter?: string[] };
  careerLevelWhy?: string[];
  careerLevelIncludes?: string[];
  promotionTriggers?: string[];
  expectedBenefits?: string[];
  /** Plain-language scheduling guidance — never show numeric scores in UI. */
  schedulingRecommendation?: string;
};

export type SiteGlueConsultantState = {
  websiteUrl: string;
  businessName: string;
  /** Extended Knowledge / company profile markdown from website scrape (shown before snapshot). */
  companyProfileMarkdown: string;
  businessType?: string;
  industry?: string;
  services: string[];
  locations: string[];
  departments: string[];
  teamMembers: string[];
  businessGoals: string[];
  /** True when goals/channels were seeded from the marketing homepage hero intake. */
  homepageIntakeApplied?: boolean;
  detectedChannels: string[];
  recommendedChannels: string[];
  appointmentRelevant: boolean;
  schedulingRelevance: SchedulingRelevance;
  schedulingOpportunityScore: number;
  schedulingScoreWhy: string[];
  currentSchedulingMethod: string;
  detectedSchedulingSystem: string | null;
  recommendedSchedulingLevel: SchedulingLevel;
  recommendedAppointmentRoles: string[];
  recommendedAppointmentWorkflows: string[];
  recommendedSchedulingRules: string[];
  recommendedIntegrations: string[];
  integrationPath: IntegrationPathKind | null;
  integrationPlatform: string | null;
  interimWorkflow: string | null;
  recommendedAgents: RecommendedAiEmployee[];
  recommendedPlan: AiEmployeePlanId | null;
  /** Authoritative plan chosen in Budget step (may match recommendedPlan). */
  selectedPlan: AiEmployeePlanId | null;
  budgetConfirmed: boolean;
  employeeName: string;
  employeePersonality: EmployeePersonality | null;
  employeePhotoChoice: EmployeePhotoChoice | null;
  /** Selected preset avatar URL for Meet Your AI Employee. */
  employeeProfileImageUrl: string;
  planRecommendationReasons: string[];
  estimatedImpact: EstimatedImpactItem[];
  snapshotFindings: BusinessSnapshotFinding[];
  report: AiOpportunityReportPayload | null;
  reportStatus: "none" | "draft" | "ready";
  onboardingProgress: number;
  lastCompletedStep: SiteGlueConsultantStep | null;
  emailFollowUpConsent: boolean;
  customMcpRequestId: string | null;
  customMcpStatus: CustomMcpRequestStatus | null;
  step: SiteGlueConsultantStep;
};

export const SITEGLUE_CONSULTANT_SESSION_KEY = "egpt-siteglue-consultant-state";

export const SITEGLUE_CHANNEL_OPTIONS = [
  "Website Chat",
  "Phone Calls",
  "Web Voice",
  "SMS",
  "Email",
  "Video Calls",
  "WhatsApp",
  "Messenger",
] as const;

/**
 * Chip / natural-language fallback when AI draft is missing.
 * Uses the same top-10 business outcomes as Trish intake, plus "Something else".
 */
export function getConsultantGoalOptions(industryOrVertical?: string | null): string[] {
  const resolved = resolveSiteGlueIndustry(industryOrVertical);
  const outcomes = getSiteGlueIndustryBusinessOutcomes(resolved ?? industryOrVertical);
  return [...outcomes, "Something else"];
}

/** Universal department chips; AI draft should override with org-specific teams. */
export function getConsultantDepartmentSuggestions(
  _industryOrVertical?: string | null,
): string[] {
  // Neutral defaults — never assume healthcare Front Desk / Support.
  return ["Customer Service", "Sales", "Operations", "Leadership"];
}

export function createEmptyConsultantState(
  partial?: Partial<SiteGlueConsultantState>,
): SiteGlueConsultantState {
  return {
    websiteUrl: "",
    businessName: "",
    companyProfileMarkdown: "",
    services: [],
    locations: [],
    departments: [],
    teamMembers: [],
    businessGoals: [],
    detectedChannels: [],
    recommendedChannels: ["Website Chat"],
    appointmentRelevant: false,
    schedulingRelevance: "not_applicable",
    schedulingOpportunityScore: 0,
    schedulingScoreWhy: [],
    currentSchedulingMethod: "No scheduling method detected",
    detectedSchedulingSystem: null,
    recommendedSchedulingLevel: "none",
    recommendedAppointmentRoles: [],
    recommendedAppointmentWorkflows: [],
    recommendedSchedulingRules: [],
    recommendedIntegrations: [],
    integrationPath: null,
    integrationPlatform: null,
    interimWorkflow: null,
    recommendedAgents: [],
    recommendedPlan: null,
    selectedPlan: null,
    budgetConfirmed: false,
    employeeName: "",
    employeePersonality: null,
    employeePhotoChoice: null,
    employeeProfileImageUrl: "",
    planRecommendationReasons: [],
    estimatedImpact: [],
    snapshotFindings: [],
    report: null,
    reportStatus: "none",
    onboardingProgress: 0,
    lastCompletedStep: null,
    emailFollowUpConsent: false,
    customMcpRequestId: null,
    customMcpStatus: null,
    step: "intake_industry",
    ...partial,
  };
}

/** Canonical PRD v2 progress order (pricing before configure). */
export const SITEGLUE_CONSULTANT_STEP_ORDER: SiteGlueConsultantStep[] = [
  "intake_industry",
  "intake_goals",
  "intake_channels",
  "intake_scheduling",
  "intake_platform",
  "intake_demo",
  "website",
  "agent_profile",
  "agent_job_title",
  "analyzing",
  "building_knowledge",
  "snapshot",
  "goals",
  "report_draft",
  "recommended_agents",
  "budget",
  "meet_employee",
  "channels",
  "scheduling_relevance",
  "scheduling_method",
  "scheduling_level",
  "integration_path",
  "appointment_roles",
  "workflows",
  "org_structure",
  "plan",
  "report",
  "review",
  "sign_up",
  "complete",
];

export function migrateConsultantState(
  raw: Partial<SiteGlueConsultantState> & { step?: string },
): SiteGlueConsultantState {
  const base = createEmptyConsultantState();
  const merged = { ...base, ...raw } as SiteGlueConsultantState;

  if (merged.step === "plan") {
    // Legacy order put plan after configure — skip budget if already configured.
    if (merged.recommendedChannels.length > 0 || merged.departments.length > 0) {
      merged.selectedPlan = merged.selectedPlan ?? merged.recommendedPlan;
      merged.budgetConfirmed = true;
      merged.step = merged.employeeName ? "report" : "meet_employee";
    } else if (!merged.budgetConfirmed && !merged.selectedPlan) {
      merged.step = "budget";
    } else {
      merged.step = "meet_employee";
    }
  }

  if (!SITEGLUE_CONSULTANT_STEP_ORDER.includes(merged.step)) {
    merged.step = "intake_industry";
  }

  if (
    merged.step === "website" &&
    !merged.websiteUrl?.trim() &&
    !merged.businessName?.trim()
  ) {
    merged.step = "intake_industry";
  }

  // Do not auto-select a plan from recommendedPlan on load — plan-first onboarding
  // requires an explicit user choice (chat chips or Progress card Select).
  if (
    merged.budgetConfirmed &&
    !merged.selectedPlan &&
    merged.recommendedPlan
  ) {
    merged.selectedPlan = merged.recommendedPlan;
  }

  merged.employeeName = merged.employeeName ?? "";
  merged.employeeProfileImageUrl = merged.employeeProfileImageUrl ?? "";
  merged.companyProfileMarkdown = merged.companyProfileMarkdown ?? "";
  merged.budgetConfirmed = Boolean(merged.budgetConfirmed);
  if (merged.industry?.trim()) {
    merged.industry = resolveSiteGlueIndustry(merged.industry) ?? merged.industry.trim();
  }
  merged.onboardingProgress = consultantProgressPercent(merged.step);
  return merged;
}

export function writeConsultantState(state: SiteGlueConsultantState): void {
  if (typeof window === "undefined") return;
  try {
    const json = JSON.stringify(state);
    window.sessionStorage.setItem(SITEGLUE_CONSULTANT_SESSION_KEY, json);
    window.localStorage.setItem(SITEGLUE_CONSULTANT_SESSION_KEY, json);
  } catch {
    // ignore quota / private mode
  }
}

export function readConsultantState(): SiteGlueConsultantState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw =
      window.sessionStorage.getItem(SITEGLUE_CONSULTANT_SESSION_KEY) ??
      window.localStorage.getItem(SITEGLUE_CONSULTANT_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SiteGlueConsultantState>;
    if (!parsed || typeof parsed !== "object") return null;
    return migrateConsultantState(parsed);
  } catch {
    return null;
  }
}

export function clearConsultantState(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(SITEGLUE_CONSULTANT_SESSION_KEY);
    window.localStorage.removeItem(SITEGLUE_CONSULTANT_SESSION_KEY);
    // Draft bubbles / in-progress composer state for Trish consultant.
    window.sessionStorage.removeItem("egpt-siteglue-consultant-draft");
  } catch {
    // ignore
  }
}

export function consultantProgressPercent(step: SiteGlueConsultantStep): number {
  const order = SITEGLUE_CONSULTANT_STEP_ORDER;
  const idx = order.indexOf(step);
  if (idx < 0) return 0;
  return Math.round((idx / (order.length - 1)) * 100);
}

/** True once Trish reaches the budget / pricing-plans conversation (or later). */
export function isSiteGluePricingStepReached(step: SiteGlueConsultantStep): boolean {
  const budgetIdx = SITEGLUE_CONSULTANT_STEP_ORDER.indexOf("budget");
  const idx = SITEGLUE_CONSULTANT_STEP_ORDER.indexOf(step);
  return budgetIdx >= 0 && idx >= budgetIdx;
}

export function schedulingLevelLabel(level: SchedulingLevel): string {
  switch (level) {
    case "none":
      return "No Scheduling Needed";
    case "appointment_request":
      return "Appointment Request Capture";
    case "basic":
      return "Basic Scheduling";
    case "advanced":
      return "Advanced Scheduling";
    case "enterprise":
      return "Enterprise Scheduling";
  }
}

export function relevanceLabel(r: SchedulingRelevance): string {
  switch (r) {
    case "high":
      return "High";
    case "moderate":
      return "Moderate";
    case "low":
      return "Low";
    case "not_applicable":
      return "Not Applicable";
  }
}

export function priorityLabel(p: ConsultantAgentPriority): string {
  switch (p) {
    case "recommended_first":
      return "Recommended First";
    case "high_value":
      return "High Value";
    case "optional":
      return "Optional";
    case "future":
      return "Future Opportunity";
  }
}
